import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'
import BaristaArchive from './BaristaArchive'

export default function StaffManagementInfo({ adminStaffData }) {
    const [baristas, setBaristas] = useState([])
    const [loading, setLoading] = useState(true)
    const [showAddModal, setShowAddModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [showViewModal, setShowViewModal] = useState(false)
    const [editingBarista, setEditingBarista] = useState(null)
    const [viewingBarista, setViewingBarista] = useState(null)
    const [originalFormData, setOriginalFormData] = useState(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [employmentTypeFilter, setEmploymentTypeFilter] = useState('All')
    const [showArchiveView, setShowArchiveView] = useState(false)

    const defaultForm = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        employmentType: '',
        startDate: '',
        status: 'Active',
    }

    const [formData, setFormData] = useState(defaultForm)

    // ── Swal button styles (same as StaffManagement) ──────────────────────────
    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
            .swal2-confirm { background-color: #FF6F3C !important; border: none !important; }
            .swal2-confirm:hover { background-color: #ff4c0b !important; }
            .swal2-cancel { background-color: #6b7280 !important; border: none !important; }
            .swal2-cancel:hover { background-color: #5a5a5a !important; }
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        if (adminStaffData?.branch_id) fetchBaristas()
    }, [adminStaffData?.branch_id])

    useEffect(() => {
        if (!showArchiveView && adminStaffData?.branch_id) fetchBaristas()
    }, [showArchiveView])

    // ── Friendly errors (same pattern as StaffManagement) ────────────────────
    const getFriendlyError = (err) => {
        const msg = err?.message?.toLowerCase() || ''
        if (err?.code === '23505' || msg.includes('duplicate key') || msg.includes('unique'))
            return 'A barista with this email already exists. Please use a different email.'
        if (msg.includes('network') || msg.includes('fetch'))
            return 'Network error. Please check your internet connection and try again.'
        if (msg.includes('jwt') || msg.includes('auth') || msg.includes('unauthorized'))
            return 'Your session has expired. Please log in again.'
        if (msg.includes('permission') || msg.includes('rls'))
            return "You don't have permission to perform this action."
        return 'Something went wrong. Please try again or contact support.'
    }

    // ── Fetch baristas for this branch ────────────────────────────────────────
    const fetchBaristas = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('barista_info')
                .select('*')
                .eq('branch_id', adminStaffData.branch_id)
                .order('start_date', { ascending: false })

            if (error) throw error
            setBaristas(data || [])
        } catch (err) {
            console.error('Error fetching baristas:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Load Baristas',
                text: getFriendlyError(err),
                confirmButtonColor: '#FF6F3C',
            })
        } finally {
            setLoading(false)
        }
    }

    // ── Swap to the archive view ───────────────────────────────────────────────
    if (showArchiveView) {
        return (
            <BaristaArchive
                branchId={adminStaffData.branch_id}
                onBack={() => setShowArchiveView(false)}
                onBaristaRestored={fetchBaristas}
            />
        )
    }

    // ── Validation (same rules as StaffManagement) ────────────────────────────
    const validateForm = () => {
        const nameRegex = /^[a-zA-Z\s]+$/

        if (formData.firstName.length > 30) {
            Swal.fire({ icon: 'warning', title: 'Invalid First Name', text: 'First name must not exceed 30 characters.', confirmButtonColor: '#FF6F3C' })
            return false
        }
        if (!nameRegex.test(formData.firstName)) {
            Swal.fire({ icon: 'warning', title: 'Invalid First Name', text: 'First name must contain only letters.', confirmButtonColor: '#FF6F3C' })
            return false
        }
        if (formData.lastName.length > 30) {
            Swal.fire({ icon: 'warning', title: 'Invalid Last Name', text: 'Last name must not exceed 30 characters.', confirmButtonColor: '#FF6F3C' })
            return false
        }
        if (!nameRegex.test(formData.lastName)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Last Name', text: 'Last name must contain only letters.', confirmButtonColor: '#FF6F3C' })
            return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Email', text: 'Please enter a valid email address.', confirmButtonColor: '#FF6F3C' })
            return false
        }
        const cleanPhone = formData.phoneNumber.replace(/[\s-]/g, '')
        if (!/^(09|\+639)\d{9}$/.test(cleanPhone)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Phone Number', text: 'Please enter a valid Philippine phone number (e.g. 09123456789).', confirmButtonColor: '#FF6F3C' })
            return false
        }
        const selectedDate = new Date(formData.startDate)
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        if (selectedDate > today) {
            Swal.fire({ icon: 'warning', title: 'Invalid Start Date', text: 'Start date cannot be in the future.', confirmButtonColor: '#FF6F3C' })
            return false
        }
        return true
    }

    const requiredFieldsFilled = () =>
        formData.firstName && formData.lastName && formData.email &&
        formData.phoneNumber && formData.employmentType && formData.startDate

    // ── Add barista ───────────────────────────────────────────────────────────
    const handleAddBarista = async () => {
        if (!requiredFieldsFilled()) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in all required fields.', confirmButtonColor: '#FF6F3C' })
            return
        }
        if (!validateForm()) return

        try {
            const { error } = await supabase
                .from('barista_info')
                .insert([{
                    first_name: formData.firstName.trim(),
                    last_name: formData.lastName.trim(),
                    email: formData.email.trim().toLowerCase(),
                    phone_number: formData.phoneNumber.trim(),
                    employment_type: formData.employmentType,
                    start_date: formData.startDate,
                    status: 'Active',
                    branch_id: adminStaffData.branch_id,  // always this admin's branch
                }])

            if (error) throw error

            setFormData(defaultForm)
            setShowAddModal(false)

            Swal.fire({
                icon: 'success',
                title: 'Barista Added!',
                text: `${formData.firstName} ${formData.lastName} has been added to your branch.`,
                confirmButtonColor: '#FF6F3C',
                timer: 3000,
            })

            fetchBaristas()
        } catch (err) {
            console.error('Error adding barista:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Add Barista', text: getFriendlyError(err), confirmButtonColor: '#FF6F3C' })
        }
    }

    // ── Edit barista ──────────────────────────────────────────────────────────
    const handleEditClick = (barista) => {
        setEditingBarista(barista)
        const original = {
            firstName: barista.first_name,
            lastName: barista.last_name,
            email: barista.email,
            phoneNumber: barista.phone_number,
            employmentType: barista.employment_type,
            startDate: barista.start_date,
            status: barista.status,
        }
        setFormData(original)
        setOriginalFormData(original)
        setShowEditModal(true)
    }

    const handleUpdateBarista = async () => {
        if (!requiredFieldsFilled()) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in all required fields.', confirmButtonColor: '#FF6F3C' })
            return
        }
        if (!validateForm()) return

        try {
            const { error } = await supabase
                .from('barista_info')
                .update({
                    first_name: formData.firstName.trim(),
                    last_name: formData.lastName.trim(),
                    email: formData.email.trim().toLowerCase(),
                    phone_number: formData.phoneNumber.trim(),
                    employment_type: formData.employmentType,
                    start_date: formData.startDate,
                    status: formData.status,
                })
                .eq('barista_id', editingBarista.barista_id)

            if (error) throw error

            setShowEditModal(false)
            setEditingBarista(null)
            setFormData(defaultForm)

            Swal.fire({
                icon: 'success',
                title: 'Barista Updated!',
                text: `${formData.firstName} ${formData.lastName}'s information has been updated.`,
                confirmButtonColor: '#FF6F3C',
                timer: 3000,
            })

            fetchBaristas()
        } catch (err) {
            console.error('Error updating barista:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Update Barista', text: getFriendlyError(err), confirmButtonColor: '#FF6F3C' })
        }
    }

    // ── Archive barista (set status Inactive) ─────────────────────────────────
    const handleArchiveClick = (barista) => {
        Swal.fire({
            icon: 'warning',
            title: `Archive ${barista.first_name} ${barista.last_name}?`,
            text: "This will set their status to Inactive. You can reactivate them later by editing.",
            showCancelButton: true,
            confirmButtonText: 'Yes, archive',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#FF6F3C',
        }).then(async (result) => {
            if (!result.isConfirmed) return
            try {
                const { error } = await supabase
                    .from('barista_info')
                    .update({ status: 'Inactive' })
                    .eq('barista_id', barista.barista_id)

                if (error) throw error

                Swal.fire({ icon: 'success', title: 'Barista Archived', text: `${barista.first_name} ${barista.last_name} has been set to Inactive.`, confirmButtonColor: '#FF6F3C', timer: 3000 })
                fetchBaristas()
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Failed to Archive', text: getFriendlyError(err), confirmButtonColor: '#FF6F3C' })
            }
        })
    }

    // ── Has changes check (for edit button) ───────────────────────────────────
    const hasChanges = originalFormData
        ? Object.keys(originalFormData).some(key => formData[key] !== originalFormData[key])
        : false

    // ── Filtered list (excludes archived/Inactive baristas) ────────────────────
    const filtered = baristas.filter(b => {
        const matchesSearch = `${b.first_name} ${b.last_name} ${b.email}`.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesEmploymentType = employmentTypeFilter === 'All' || b.employment_type === employmentTypeFilter
        return matchesSearch && matchesEmploymentType && b.status !== 'Inactive'
    })

    // ── Shared form fields (used in both Add and Edit modals) ─────────────────
    const renderFormFields = () => (
        <div className="px-6 py-6 space-y-6">
            {/* Personal Information */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\b\w/g, l => l.toUpperCase()).replace(/[^a-zA-Z\s]/g, '')
                                setFormData({ ...formData, firstName: val })
                            }}
                            maxLength={30}
                            placeholder="e.g. Maria"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                        <p className="text-xs text-gray-500 mt-1">{formData.firstName.length}/30 characters</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\b\w/g, l => l.toUpperCase()).replace(/[^a-zA-Z\s]/g, '')
                                setFormData({ ...formData, lastName: val })
                            }}
                            maxLength={30}
                            placeholder="e.g. Santos"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                        <p className="text-xs text-gray-500 mt-1">{formData.lastName.length}/30 characters</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            maxLength={100}
                            placeholder="e.g. maria@example.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                        <p className="text-xs text-gray-500 mt-1">{formData.email.length}/100 characters</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/[^0-9+]/g, '') })}
                            maxLength={13}
                            placeholder="e.g. 09171234567"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                        <p className="text-xs text-gray-500 mt-1">{formData.phoneNumber.length}/13 characters</p>
                    </div>
                </div>
            </div>

            {/* Employment Details */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Employment Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                            max={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Employment Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={formData.employmentType}
                            onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        >
                            <option value="">Select employment type...</option>
                            <option value="Full-time">Full-time</option>
                            <option value="Part-time">Part-time</option>
                            <option value="Contract">Contract</option>
                        </select>
                    </div>

                    {/* Status only shown in Edit */}
                    {showEditModal && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="On Leave">On Leave</option>
                            </select>
                        </div>
                    )}
                </div>

                {/* Branch (read-only, always this admin's branch) */}
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Branch</label>
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-gray-400">
                            <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5"/>
                        </svg>
                        Automatically assigned to your branch
                    </div>
                </div>
            </div>
        </div>
    )

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8">

            <div className="bg-white rounded-lg shadow-sm">
                {/* Header section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Branch Baristas</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Manage baristas registered under your branch
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                            <button
                                onClick={() => setShowArchiveView(true)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 flex items-center justify-center gap-2 w-full sm:w-auto"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z"/>
                                </svg>
                                View Archive
                            </button>
                            <button
                                onClick={() => { setFormData(defaultForm); setShowAddModal(true) }}
                                className="px-4 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] flex items-center justify-center gap-2 w-full sm:w-auto"
                            >
                                <span>+</span> Add Barista
                            </button>
                        </div>
                    </div>
                </div>

                {/* Search + filters section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="relative max-w-md">
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6"/>
                            </svg>
                        </span>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:gap-4 mt-4">
                        {['All', 'Full-time', 'Part-time', 'Contract'].map((type) => (
                            <button
                                key={type}
                                onClick={() => setEmploymentTypeFilter(type)}
                                className={`px-4 py-2 rounded-lg font-medium ${
                                    employmentTypeFilter === type
                                        ? 'bg-[#FF6F3C] text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barista</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employment Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading baristas...</td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                        {searchQuery ? 'No baristas match your search.' : 'No baristas added yet for this branch.'}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((barista) => (
                                    <tr key={barista.barista_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="font-medium text-gray-800">{barista.first_name} {barista.last_name}</p>
                                            <p className="text-sm text-gray-500">{barista.email}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">{barista.employment_type}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                barista.status === 'Active'
                                                    ? 'bg-green-100 text-green-800'
                                                    : barista.status === 'On Leave'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {barista.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                                            {new Date(barista.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                {/* Edit */}
                                                <button onClick={() => handleEditClick(barista)}
                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z"/>
                                                    </svg>
                                                </button>
                                                {/* Archive */}
                                                <button onClick={() => handleArchiveClick(barista)}
                                                    className="p-1 text-orange-600 hover:bg-orange-50 rounded" title="Archive">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z"/>
                                                    </svg>
                                                </button>
                                                {/* View */}
                                                <button onClick={() => { setViewingBarista(barista); setShowViewModal(true) }}
                                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded" title="View Details">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Add Modal ──────────────────────────────────────────────────────── */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowAddModal(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Add New Barista</h2>
                            <p className="text-sm text-gray-600 mt-1">Fill in the details to register a new barista for your branch</p>
                        </div>
                        {renderFormFields()}
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setShowAddModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Cancel
                            </button>
                            <button onClick={handleAddBarista}
                                className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b]">
                                Add Barista
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Edit Modal ─────────────────────────────────────────────────────── */}
            {showEditModal && editingBarista && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowEditModal(false); setEditingBarista(null) }} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Edit Barista</h2>
                            <p className="text-sm text-gray-600 mt-1">Update barista information</p>
                        </div>
                        {renderFormFields()}
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => { setShowEditModal(false); setEditingBarista(null) }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateBarista}
                                disabled={!hasChanges}
                                className={`px-6 py-2 text-white rounded-lg font-medium transition-colors ${
                                    hasChanges ? 'bg-[#FF6F3C] hover:bg-[#ff4c0b] cursor-pointer' : 'bg-gray-300 cursor-not-allowed'
                                }`}>
                                {hasChanges ? 'Update Barista' : 'No Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── View Modal ─────────────────────────────────────────────────────── */}
            {showViewModal && viewingBarista && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowViewModal(false); setViewingBarista(null) }} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Barista Details</h2>
                            <p className="text-sm text-gray-600 mt-1">Full information for this barista</p>
                        </div>
                        <div className="px-6 py-6 space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">First Name</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.first_name}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Last Name</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.last_name}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.email}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Phone Number</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.phone_number}</p></div>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Employment Details</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Employment Type</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.employment_type}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Start Date</label>
                                        <p className="text-gray-800 font-medium">
                                            {new Date(viewingBarista.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                        </p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                            viewingBarista.status === 'Active' ? 'bg-green-100 text-green-800'
                                            : viewingBarista.status === 'On Leave' ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>{viewingBarista.status}</span>
                                    </div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Barista ID</label>
                                        <p className="text-gray-800 font-mono text-sm">{viewingBarista.barista_id}</p></div>
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => { setShowViewModal(false); setViewingBarista(null) }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Close
                            </button>
                            <button onClick={() => { setShowViewModal(false); setViewingBarista(null); handleEditClick(viewingBarista) }}
                                className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b]">
                                Edit Barista
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}